import React from "react"
import cx from "classnames"

// =============================================================================
// Root
// =============================================================================

export type AppLayoutProps = {
  /** The navigation sidebar followed by an `AppLayout.Content`. */
  children?: React.ReactNode
  className?: string
}

/**
 * Top-level app shell. A full-height flex row that lays out the navigation
 * sidebar beside the main content card.
 *
 * Self-sufficient: it fills its width, establishes its own viewport height
 * (`h-svh`), and paints the app background, so it doesn't depend on an ancestor
 * to size or colour it. The navigation sidebar is passed as the first child (it
 * owns its own `<nav>` landmark) and the page content goes inside
 * `AppLayout.Content`.
 *
 * @example
 * import { AppLayout } from "@kit/design/app-layout"
 *
 * <AppLayout>
 *   <AppNavigation mobile={isMobile} />
 *   <AppLayout.Content mobile={isMobile}>
 *     <Page>...</Page>
 *   </AppLayout.Content>
 * </AppLayout>
 */
function AppLayoutRoot({ children, className }: AppLayoutProps) {
  return (
    <div
      className={cx(
        "relative flex w-full h-svh overflow-hidden bg-warm-white-100",
        className
      )}
    >
      {children}
    </div>
  )
}

AppLayoutRoot.displayName = "AppLayout"

// =============================================================================
// Content
// =============================================================================

export type AppLayoutContentProps = {
  /** Main content — typically a `Page`, optionally preceded by banners. */
  children?: React.ReactNode
  className?: string
  /**
   * Pass `true` when the sidebar is in mobile mode (slides in/out rather than
   * collapsing to icon-only). Prevents the content from shrinking when the
   * sidebar opens.
   */
  mobile?: boolean
}

/**
 * The content card beside the navigation. Owns the vertical scroll for the
 * page that lives inside it. The `<main>` landmark lives deeper, on `PageBody`,
 * so this is a plain `<div>`.
 */
function AppLayoutContent({
  children,
  className,
  mobile
}: AppLayoutContentProps) {
  return (
    <div
      className={cx(
        "my-2 mr-2 flex-1 rounded-[10px] border border-gray-200 bg-white overflow-y-auto",
        mobile && "shrink-0 basis-full",
        className
      )}
    >
      {children}
    </div>
  )
}

AppLayoutContent.displayName = "AppLayout.Content"

// =============================================================================
// Component
// =============================================================================

export const AppLayout = Object.assign(AppLayoutRoot, {
  Content: AppLayoutContent
})

export default AppLayout
